import React, { FC, Fragment, memo, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { handleRichText } from '@/utils/handleRichText';
import { ECOM_PROMOTIONS, SHARED_BANNER_MODAL } from '@/constants/homePage';
import { useGetQuoteConfig } from '@/hooks/use-get-quote-config';
import LazyImage from './lazy-image';
import { PRODUCT_MAP } from '@/constants/setting';
import * as cheerio from 'cheerio';
import CrossSell from '../CrossSell/CrossSell';
import PromotionCard from './promotion-card';
import QuoteCardVprime from '../QuoteCardVprime';
import QuoteCardMyMillion from '../QuoteCardMyMillion';
import QuoteCardMyTermPlus from '../QuoteCardMyTermPlus';
import QuoteCardMyCover from '../QuoteCardMyCover';

type Props = {
  pageData: any;
  language: any;
};
const RichText: FC<Props> = memo(props => {
  const { pageData, language } = props;

  const getQuoteConfig = useGetQuoteConfig() as any;
  const disableVprime = getQuoteConfig?.VPRIME;
  const disableMyMillion = getQuoteConfig?.MYMILLION;
  const disableMyTermPlus = getQuoteConfig?.MYTERM;
  const disableMyCover = getQuoteConfig?.MYCOVER;

  const getArticleRenderList = () => {
    let renderList: never[] = [];
    if (pageData && pageData.modular_blocks) {
      renderList = pageData?.modular_blocks?.filter((obj: any) => {
        const keys = Object.keys(obj);
        return (
          keys.includes('rich_text_editor') ||
          keys.includes('button') ||
          keys.includes('additional_information') ||
          keys.includes('promotional_banner') ||
          keys.includes('simplified_related_reading') ||
          keys.includes('promotional_message') ||
          keys.includes('block') ||
          keys.includes('cross_sell') ||
          keys.includes('quote_product_card')
        );
      });
    }
    return renderList || [];
  };

  const renderTargetQuoteCard = (itemData: any, index: number) => {
    const quoteCardReference = itemData['quote_product_card'] ?? {};
    const quoteProductCard =
      quoteCardReference?.quote_product_card && quoteCardReference?.quote_product_card?.length
        ? quoteCardReference?.quote_product_card[0]
        : {};
    let configAndPromo = pageData.quoteCards?.filter((item: any) => item?.configV2?.product_identifier?.product_id === quoteProductCard?.product_identifier?.product_id)

    switch (quoteProductCard?.product_identifier?.product_id) {
      case 'VPRIME':
        if (disableVprime === 'false') {
          return (
            <div key={index + 100} style={{ marginBottom: 48 }}>
              <QuoteCardVprime
                quoteCardData={quoteProductCard}
                promotionData={configAndPromo?.[0]?.defaultPromotionData || {}}
                productConfig={configAndPromo?.[0]?.configV2 || {}}
              />
            </div>
          );
        }
        break;
      case 'MMP':
        if (disableMyMillion === 'false') {
          return (
            <div key={index + 100} style={{ marginBottom: 48 }}>
              <QuoteCardMyMillion
                quoteCardData={quoteProductCard}
                promotionData={configAndPromo?.[0]?.defaultPromotionData || {}}
                productConfig={configAndPromo?.[0]?.configV2 || {}}
              />
            </div>
          );
        }
        break;
      case 'MTR':
        if (disableMyTermPlus === 'false') {
          return (
            <div key={index + 100} style={{ marginBottom: 48 }}>
              <QuoteCardMyTermPlus
                quoteCardData={quoteProductCard}
                promotionData={configAndPromo?.[0]?.defaultPromotionData || {}}
                productConfig={configAndPromo?.[0]?.configV2 || {}}
              />
            </div>
          );
        }
        break;
      case 'BIGT':
        if (disableMyCover === 'false') {
          return (
            <div key={index + 100} style={{ marginBottom: 48 }}>
              <QuoteCardMyCover
                quoteCardData={quoteProductCard}
                promotionData={configAndPromo?.[0]?.defaultPromotionData || {}}
                productConfig={configAndPromo?.[0]?.configV2 || {}}
              />
            </div>
          );
        }
        break;
    }
  };

  const handleOpenLink = (item: any) => {
    const url =
      item?.button?.button_url?.split('?').length > 0
        ? item?.button?.button_url?.split('?')[0]
        : '';
    if (!url) {
      return;
    }
    window?.dataLayer?.push({
      event: 'productClick',
      ecommerce: {
        click: {
          actionField: { list: 'Blog Article' },
          products: [
            {
              id: PRODUCT_MAP[url]?.id,
              name: PRODUCT_MAP[url]?.name,
              category: PRODUCT_MAP[url]?.category,
              brand: PRODUCT_MAP[url]?.brand
            }
          ]
        }
      }
    });
  };

  const handleClick = (e: any) => {
    if (
      e.target.nodeName == 'H4' ||
      e.target.nodeName == 'H5' ||
      e.target.nodeName == 'H6' ||
      e.target.parentNode.nodeName == 'H4' ||
      e.target.parentNode.nodeName == 'H5' ||
      e.target.parentNode.nodeName == 'H6'
    ) {
      const url = e?.target?.href
        ? e?.target?.href
        : e.target.childNodes.length > 0
          ? e.target.childNodes[0].href
          : '';
      if (!url) {
        return;
      }

      window?.dataLayer?.push({
        event: 'productClick',
        ecommerce: {
          click: {
            actionField: { list: 'Blog Article' },
            products: [
              {
                id: PRODUCT_MAP[url]?.id,
                name: PRODUCT_MAP[url]?.name,
                category: PRODUCT_MAP[url]?.category,
                brand: PRODUCT_MAP[url]?.brand
              }
            ]
          }
        }
      });
    }
  };

  useEffect(() => {
    const dom: any = document.getElementById('rich_text_wrapper');
    if (dom) {
      const aDom = dom.querySelectorAll('a');
      for (let i = 0; i < aDom.length; i++) {
        aDom[i].addEventListener('click', function () {
          window?.dataLayer?.push({
            event: 'buttonClick',
            buttonTitle: 'article_hyperlink',
            description: aDom[i].text,
            destinationUrl: aDom[i].href,
            pageURL: window.location.href,
            componentName: 'article hyperlink'
          });
        });
      }
    }
  }, []);

  const extractPlainText = (html: any) => {
    const $ = cheerio.load(html);

    $('h2').each((index, element) => {
      const $h2 = $(element);
      const id = $h2.text();
      $h2.attr('id', id.replace(/\s/g, ''));
    });

    const updatedHtml = $.html();

    return updatedHtml;
  };

  const handleImgInLink = (ElementStr: string) => {
    const $ = cheerio.load(ElementStr);

    $('a').each((index, element) => {
      const img = $(element).find('img');
      if (img.length > 0) {
        $(element).addClass('imgIn');
      }
    });

    const res = $.html();
    var modifiedStr = extractPlainText(res);

    return modifiedStr;
  };

  const simplifiedRelatedReadingClick = (url: string, title: any) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'inarticle_related_article',
      description: title,
      destinationUrl: url,
      pageURL: window.location.href,
      componentName: 'inarticle related article'
    });
  };

  const handleGABanner = (description: string, destinationUrl: string) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'blog_banner_sticky',
      description,
      destinationUrl,
      pageURL: window.location.href,
      componentName: 'article promotion banner'
    });
  };

  const currentDate = new Date();

  return (
    <div className={'Article_Render_List'}>
      {getArticleRenderList().map((item: any, index: number) => {
        if (item['rich_text_editor']) {
          const regex =
            /(<table\b[^>]*>)([\s\S]*?)(<\/table>)|<p>&nbsp;<\/p>|<h2[^>]*><\/h2>|<p[^>]*>(?:(?!\S)|<\s*\/?\s*\w+\s*\/?\s*>)*<\/p>|<p[^>]*>(&nbsp;|\s)*<\/p>|<h2>&nbsp;<\/h2>/g;
          const result = item.rich_text_editor.rich_text_editor
            .replace(regex, (match: any, p1: any, p2: any, p3: any) => {
              if (p1) {
                if (p1.includes('table')) {
                  p2 = p2
                    .replaceAll('✓', '<Image src="/blog/icon/Tick.svg"/>')
                    .replaceAll('×', '<Image src="/blog/icon/ClosePrimary.svg"/>');
                }
                return `<div>${p1}${p2}${p3}</div>`;
              } else {
                return '';
              }
            })
            .replace(
              /<h6\b[^>]*>([\s\S]*?)<\/h6>/g,
              '<div style="display: flex; justify-content: center;">$&</div>'
            )
            .replace(/&nbsp;/g, ' ');
          return (
            <div
              id={'rich_text_wrapper'}
              key={index}
              onClick={handleClick}
              dangerouslySetInnerHTML={{
                __html: handleRichText(handleImgInLink(result))
              }}
            ></div>
          );
        }

        if (
          item?.promotional_message?.promotional_message?.length > 0 &&
          item?.promotional_message?.promotional_message[0]?.rich_text_editor
        ) {
          const regex =
            /(<table\b[^>]*>)([\s\S]*?)(<\/table>)|<p>&nbsp;<\/p>|<h2[^>]*><\/h2>|<p[^>]*>(?:(?!\S)|<\s*\/?\s*\w+\s*\/?\s*>)*<\/p>|<p[^>]*>(&nbsp;|\s)*<\/p>|<h2>&nbsp;<\/h2>/g;
          const result = item?.promotional_message?.promotional_message[0]?.rich_text_editor
            ?.replace(regex, (match: any, p1: any, p2: any, p3: any) => {
              if (p1) {
                if (p1.includes('table')) {
                  p2 = p2
                    .replaceAll('✓', '<Image src="/blog/icon/Tick.svg"/>')
                    .replaceAll('×', '<Image src="/blog/icon/ClosePrimary.svg"/>');
                }
                return `<div>${p1}${p2}${p3}</div>`;
              } else {
                return '';
              }
            })
            .replace(
              /<h6\b[^>]*>([\s\S]*?)<\/h6>/g,
              '<div style="display: flex; justify-content: center;">$&</div>'
            )
            .replace(/&nbsp;/g, ' ');
          return (
            <div
              id={'rich_text_wrapper'}
              key={index}
              onClick={handleClick}
              dangerouslySetInnerHTML={{
                __html: handleRichText(handleImgInLink(`<div>${result}</div>`))
              }}
            ></div>
          );
        }
        if (item['quote_product_card']) {
          return <>{
            renderTargetQuoteCard(item, index)}</>;
        }
        if (item['block']) {
          return <CrossSell key={index} data={item?.block?.cross_sell[0]}></CrossSell>;
        }
        if (item['cross_sell']) {
          return <CrossSell key={index} data={item?.cross_sell?.cross_sell[0]}></CrossSell>;
        }
        if (item['additional_information']) {
          return (
            <div key={index} className={'Additional_Information'}>
              <Image
                width={24}
                height={24}
                src={item.additional_information?.icon?.url}
                className={'Additional_Information_Icon'}
                alt={''}
              />
              {item.additional_information.text}
            </div>
          );
        }
        if (item['button']) {
          const openLink = item?.button?.button_url?.replace(/\/\//g, '/') || '';
          return (
            <div key={index} className={'Rich_Text_Button_Wrapper'}>
              <Link href={openLink} onClick={() => handleOpenLink(item)}>
                <button className={'Rich_Btn'}>
                  <Image
                    src="/blog/icon/ArrowRight.svg"
                    className={'Button_Icon'}
                    alt={''}
                    width={20}
                    height={20}
                  />
                  <Image
                    src="/blog/icon/TurnRight.svg"
                    className={'Button_Icon_Hover'}
                    alt={''}
                    width={20}
                    height={20}
                  />
                  {item.button.button_label}
                </button>
              </Link>
            </div>
          );
        }
        if (
          item['promotional_banner'] &&
          item['promotional_banner']['banner_reference'] &&
          item['promotional_banner']['banner_reference'][0] &&
          item['promotional_banner']['banner_reference'][0]['_content_type_uid'] === ECOM_PROMOTIONS
        ) {
          const bannerReference = item.promotional_banner?.banner_reference || [];
          const promotionsList = bannerReference[0]?.promotion || [];
          const promotions =
            Array.isArray(promotionsList) && promotionsList.length > 0
              ? promotionsList.map((item: any) => item.promotion)
              : [];
          return (
            <div className={'promotional_banner'} key={index}>
              <div className={'promotion_card'}>
                {promotions.map((item: any, k: any) => {
                  return (
                    <PromotionCard
                      key={k}
                      promotion={item}
                      language={language}
                      className={'Article_Card'}
                    />
                  );
                })}
              </div>
            </div>
          );
        }

        if (
          item['promotional_banner'] &&
          item['promotional_banner']['banner_reference'] &&
          item['promotional_banner']['banner_reference'][0] &&
          item['promotional_banner']['banner_reference'][0]['_content_type_uid'] ===
          SHARED_BANNER_MODAL
        ) {
          const bannerReference = item.promotional_banner.banner_reference || [];
          const targetUrls = bannerReference[0]?.target_urls;
          const startDate = bannerReference[0]?.start_date
            ? new Date(bannerReference[0]?.start_date)
            : null;
          const endDate = bannerReference[0]?.end_date
            ? new Date(bannerReference[0]?.end_date)
            : null;
          const promotionType = bannerReference[0]?.type_of_promotion || [];
          const imagePcUrl = promotionType[0]?.banner?.desktop_image?.url || '';
          const imageMobileUrl = promotionType[0]?.banner?.mobile_image?.url || '';
          const redirectUrl = promotionType[0]?.banner?.redirect_url || '';
          const openInNewTab = promotionType[0]?.banner?.open_in_new_tab;
          const imageAltTag = promotionType[0]?.banner?.image_alt_text;
          const isDisplay =
            startDate && currentDate > startDate && endDate && currentDate < endDate ? true : false;
          const displayInArticleByTarget =
            targetUrls?.includes('blog_article_pages') || targetUrls?.length === 0;

          return (
            <Fragment key={index}>
              {promotionType[0]?.sticky_bar ||
                !redirectUrl ||
                !isDisplay ||
                !displayInArticleByTarget ? (
                ''
              ) : (
                <div className={'shared_banner_wrapper'} key={index}>
                  <a
                    className={'shared_banner_pc'}
                    href={redirectUrl}
                    target={openInNewTab ? '_blank' : ''}
                    onClick={() => handleGABanner(imageAltTag, redirectUrl)}
                  >
                    <LazyImage src={imagePcUrl} alt={imageAltTag} width={'100%'} height={'100%'} />
                  </a>
                  <a
                    className={'shared_banner_mobile'}
                    href={redirectUrl}
                    target={openInNewTab ? '_blank' : ''}
                    onClick={() => handleGABanner(imageAltTag, redirectUrl)}
                  >
                    <LazyImage
                      src={imageMobileUrl}
                      alt={imageAltTag}
                      width={'100%'}
                      height={'100%'}
                    />
                  </a>
                </div>
              )}
            </Fragment>
          );
        }
        if (item['simplified_related_reading']) {
          const title = item.simplified_related_reading?.title || '';
          const relatedReadings = item.simplified_related_reading?.related_readings || [];

          return (
            <div className={'Extended_Reading'} key={index}>
              <div className={'Extended_Title'}>
                {title || (language === 'zh' ? '延伸閱讀' : 'simplified related articles')}{' '}
              </div>
              <div className={'Extended_Link'}>
                {relatedReadings.map((item: any) => {
                  if (
                    item?.reference_article &&
                    item.reference_article[0] &&
                    item.reference_article[0]?.url
                  ) {
                    const article = item.reference_article[0];
                    const categoryUrl = article?.blog_snippet?.category[0]?.url;
                    const resUrl = `/${language}/blog/${categoryUrl}${article?.url}`.replace(
                      /\/\//g,
                      '/'
                    );
                    return (
                      <Link
                        href={resUrl}
                        className={'Related_Link'}
                        key={item._metadata.uid}
                        onClick={() =>
                          simplifiedRelatedReadingClick(window.location.origin + resUrl, title)
                        }
                      >
                        <Image
                          src="/blog/icon/TurnRight.svg"
                          className={'Link_Icon'}
                          alt={''}
                          width={18}
                          height={16}
                        />
                        <div className={'Title_Wrapper'}>
                          <span className={'Reading_Title'}>{article.title}</span>
                        </div>
                      </Link>
                    );
                  } else if (item.text && item?.url) {
                    return (
                      <Link
                        href={item?.url?.replace(/\/\//g, '/')}
                        className={'Related_Link'}
                        key={item._metadata.uid}
                        onClick={() => simplifiedRelatedReadingClick(item?.url, title)}
                      >
                        <Image src="/blog/icon/TurnRight.svg" className={'Link_Icon'} alt={''} width={18} height={16} />
                        <div className={'Title_Wrapper'}>
                          <span className={'Reading_Title'}>{item.text}</span>
                        </div>
                      </Link>
                    );
                  }
                })}
              </div>
            </div>
          );
        }
      })}
    </div>
  );
});

export default RichText;
